import { useEffect, useRef, useState } from 'react';
import { Input, Textarea } from '@higgins/ui';
import { IN_FIELD_ORDER, moveToNextField } from '../lib/field-order';
import { useDeferredSave } from '../lib/use-deferred-save';

interface SavedTextFieldProps {
  value: string;
  /** Called with the text a moment after typing stops, on leaving the box, and when the app is hidden or closed. */
  onSave: (text: string) => void | Promise<void>;
  label: string;
  /** Rows for a several-line box; one line when left out. */
  rows?: number;
  /** Table-cell size, for the desk; the default is sized for a finger on the iPad. */
  compact?: boolean;
  /** Called when the box is left (Return moves on and leaves it). */
  onDone?: () => void;
  id?: string;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
  maxLength?: number;
}

/** The limit for a short name or note kept in a JSON document (the API's `checkOptionScope`, option-scope.ts, refuses longer). */
export const TEXT_MAX = 200;

const COMPACT = 'h-7 bg-white px-1 py-0 text-sm md:text-sm focus-visible:ring-1 focus-visible:ring-offset-0';

/**
 * Every typed-text box in the app, iPad and desk. Keeps what is typed while
 * focused — a save coming back never overwrites the word being typed — and
 * saves it after a short pause, on leaving the box, and when the app is
 * hidden or closed, so closing the tab or minimising the app loses nothing.
 * Follows outside changes while it isn't being edited. Return moves to the
 * next field (field-order.ts).
 */
export function SavedTextField({ value, onSave, label, rows, compact = false, onDone, id, placeholder, className = '', autoFocus = false, maxLength }: SavedTextFieldProps) {
  const [text, setText] = useState(value);
  const focused = useRef(false);
  const latestValue = useRef(value);
  latestValue.current = value;
  const saver = useDeferredSave((next: string) => (next === latestValue.current ? undefined : onSave(next)));

  useEffect(() => {
    if (!focused.current && !saver.pending()) setText(value);
  }, [value, saver]);

  const shared = {
    ...IN_FIELD_ORDER,
    id,
    value: text,
    'aria-label': label,
    placeholder,
    maxLength,
    autoFocus,
    onFocus: () => (focused.current = true),
    onBlur: () => {
      focused.current = false;
      void saver.flush();
      onDone?.();
    },
  };
  const change = (next: string) => {
    setText(next);
    saver.schedule(next);
  };

  if (rows) {
    return <Textarea {...shared} rows={rows} onChange={(e) => change(e.target.value)} onKeyDown={(e) => moveToNextField(e)} className={className} />;
  }
  return (
    <Input
      {...shared}
      enterKeyHint="next"
      onChange={(e) => change(e.target.value)}
      onKeyDown={(e) => moveToNextField(e, true)}
      className={`${compact ? COMPACT : ''} ${className}`}
    />
  );
}
